import { message } from 'antd'
import { useCallback, useEffect, useState } from 'react'

export const useMainFileViewer = ({
  main_files,
  fileStore,
  loadingAnimationStore,
}) => {
  const [selectedFileToView, setSelectedFileToView] = useState(null)
  const [viewingFileAttachment, setViewingFileAttachment] = useState(null)
  const [selectedFileOriginId, setSelectedFileOriginId] = useState(null)

  const handleSelectFileToView = useCallback(
    async file => {
      if (!file?.file_id) return

      try {
        loadingAnimationStore.showSpinner(true)
        await fileStore.downloadAttachment(file.file_id)
        setSelectedFileToView(file)
        setViewingFileAttachment(fileStore.documentAttachment)

        const trueOrigin = file.origin_file_id || file.file_id
        if (trueOrigin !== selectedFileOriginId) {
          setSelectedFileOriginId(trueOrigin)
        }
      } catch (err) {
        console.error('Error loading file to view:', err)
        message.error('Không thể tải file để xem!')
      } finally {
        loadingAnimationStore.showSpinner(false)
      }
    },
    [selectedFileOriginId, fileStore, loadingAnimationStore]
  )

  const clearFileView = useCallback(() => {
    setSelectedFileToView(null)
    setViewingFileAttachment(null)
  }, [])

  const resetFileView = useCallback(() => {
    clearFileView()
    setSelectedFileOriginId(null)
  }, [clearFileView])

  const showFileAt = useCallback(
    async file => {
      if (!file?.file_id) return

      const newOrigin = file.origin_file_id || file.file_id
      setSelectedFileOriginId(newOrigin)
      setSelectedFileToView(file)
      await fileStore.downloadAttachment(file.file_id)
      setViewingFileAttachment(fileStore.documentAttachment)
    },
    [fileStore]
  )

  const refreshFileViewAtIndex = useCallback(
    async (fileIndex, documentData) => {
      const signedFile = documentData?.main_files?.[fileIndex]
      if (!signedFile) return

      const newOrigin = signedFile.origin_file_id || signedFile.file_id
      setSelectedFileOriginId(newOrigin)
      setSelectedFileToView(signedFile)
      await fileStore.downloadAttachment(signedFile.file_id)
      setViewingFileAttachment(fileStore.documentAttachment)
    },
    [fileStore]
  )

  useEffect(() => {
    if (main_files && main_files.length > 0) {
      if (!selectedFileOriginId) {
        handleSelectFileToView(main_files[0])
      } else {
        const currentFile = main_files.find(f => {
          const fileOriginId = f.origin_file_id || f.file_id
          return fileOriginId === selectedFileOriginId
        })

        if (currentFile) {
          setSelectedFileToView(currentFile)

          if (currentFile.file_id !== selectedFileToView?.file_id) {
            loadingAnimationStore.showSpinner(true)
            fileStore
              .downloadAttachment(currentFile.file_id)
              .then(() => {
                setViewingFileAttachment(fileStore.documentAttachment)
                loadingAnimationStore.showSpinner(false)
              })
              .catch(() => {
                loadingAnimationStore.showSpinner(false)
              })
          }
        } else {
          handleSelectFileToView(main_files[0])
        }
      }
    } else {
      setSelectedFileToView(null)
      setViewingFileAttachment(null)
      setSelectedFileOriginId(null)
    }
  }, [main_files, selectedFileOriginId])

  useEffect(() => {
    if (selectedFileToView && fileStore.documentAttachment) {
      setViewingFileAttachment(fileStore.documentAttachment)
    }
  }, [fileStore.documentAttachment, selectedFileToView])

  return {
    selectedFileToView,
    viewingFileAttachment,
    selectedFileOriginId,
    setSelectedFileOriginId,
    handleSelectFileToView,
    clearFileView,
    resetFileView,
    showFileAt,
    refreshFileViewAtIndex,
  }
}
